import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.76, 0, 0.24, 1];
const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  travel_date: "",
  travelers: "",
  message: "",
};
const inputCls =
  "rounded-none border-0 border-b border-foreground/25 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary h-11 text-base font-light";

export const BookingForm = ({ interest, source = "package_page" }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please tell us your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, interest, source });
      toast.success("Kadrinchey — your booking inquiry is on its way.");
      setDone(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Something went wrong. Please try again in a moment."
      );
    } finally {
      setLoading(false);
    }
  };

  if (done) return <BookingSuccess interest={interest} />;

  return (
    <form
      onSubmit={submit}
      data-testid="booking-form"
      className="border border-foreground/15 bg-background p-6 md:p-10"
    >
      <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-2">
        Reserve this journey
      </div>
      <div className="font-serif text-2xl md:text-3xl font-light mb-8">
        {interest}
      </div>

      <BookingFields form={form} update={update} />

      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 border-t border-foreground/15 pt-6">
        <p className="text-xs text-foreground/50 max-w-sm leading-relaxed">
          No payment now — this sends a booking inquiry. We confirm
          availability and exact pricing within 24 hours.
        </p>
        <Button
          type="submit"
          disabled={loading}
          data-testid="booking-submit"
          className="btn-sharp inline-flex items-center gap-3 border border-foreground bg-transparent hover:bg-transparent text-foreground rounded-none px-7 py-6 text-[11px] uppercase tracking-[0.24em] font-medium disabled:opacity-60"
        >
          {loading ? "Sending…" : "Request Booking"}
          <span aria-hidden>→</span>
        </Button>
      </div>
    </form>
  );
};

function BookingFields({ form, update }) {
  return (
    <>
      <div className="grid md:grid-cols-2 gap-5 md:gap-6">
        <Field label="01 · Your name">
          <Input required value={form.name} onChange={update("name")} data-testid="booking-name" placeholder="Jigme Dorji" className={inputCls} />
        </Field>
        <Field label="02 · Email">
          <Input required type="email" value={form.email} onChange={update("email")} data-testid="booking-email" placeholder="you@example.com" className={inputCls} />
        </Field>
        <Field label="03 · Phone (optional)">
          <Input value={form.phone} onChange={update("phone")} data-testid="booking-phone" placeholder="+91 98xxx xxxxx" className={inputCls} />
        </Field>
        <Field label="04 · Travel dates">
          <Input value={form.travel_date} onChange={update("travel_date")} data-testid="booking-date" placeholder="Oct 2026 · flexible" className={inputCls} />
        </Field>
        <Field label="05 · Travellers">
          <Input value={form.travelers} onChange={update("travelers")} data-testid="booking-travelers" placeholder="2 adults" className={inputCls} />
        </Field>
      </div>

      <div className="mt-6">
        <Field label="06 · Anything else">
          <Textarea
            value={form.message}
            onChange={update("message")}
            data-testid="booking-message"
            placeholder="Pace, room preferences, dietary needs, add-on ideas…"
            rows={3}
            className="rounded-none border border-foreground/20 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base font-light resize-none"
          />
        </Field>
      </div>
    </>
  );
}

function BookingSuccess({ interest }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: EASE }}
      data-testid="booking-success"
      className="border border-primary/40 bg-primary/5 p-10 md:p-14 text-center"
    >
      <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-5">
        Kadrinchey · Thank you
      </div>
      <h3 className="font-serif text-3xl md:text-5xl font-light leading-[0.98] tracking-tight">
        Your journey request is <em className="italic text-primary">in Paro.</em>
      </h3>
      <p className="mt-5 text-base text-foreground/70 max-w-md mx-auto font-light">
        A named curator will reply within 24 hours with availability, exact
        pricing and a first-draft itinerary for {interest}.
      </p>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.28em] text-foreground/55 block mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
